import type {
  BrowserCommand,
  BrowserCommentTarget,
  BrowserDisplaySession,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  BrowserViewport,
  DevTunnelRecord,
  BrowserRenderMode,
} from "@cmmd-forge/contracts";

import { extractLocalDevServerPreviewUrl, normalizePreviewUrl } from "~/lib/devServerPreviewUrl";
import {
  browserDisplayUrl,
  isBlankBrowserUrl,
  isDevTunnelPreviewUrl,
  resolveBrowserRenderMode,
} from "./BrowserPreviewPanel.url";
import type {
  BrowserBusyState,
  BrowserConsoleSummary,
  BrowserDisplayFrameSource,
  BrowserLiveDisplayState,
  BrowserPreviewObservedText,
  BrowserPreviewStateChromeVm,
  BrowserPreviewStatus,
} from "./BrowserPreviewPanel.logic";
import { normalizeBrowserDisplayForPreview } from "./BrowserPreviewPanel.remoteWebRtcDisplay";

export function browserRememberedAddress(
  input: { rawAddressValue: string; commandUrl: string },
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): string {
  const normalizedRawAddress = normalizePreviewUrl(input.rawAddressValue);
  if (normalizedRawAddress && !isDevTunnelPreviewUrl(normalizedRawAddress)) {
    return normalizedRawAddress;
  }
  return browserDisplayUrl(input.commandUrl, devTunnelRecords) ?? input.commandUrl;
}

export function browserTabDisplayLabel(
  tab: BrowserTabSnapshot,
  index: number,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): string {
  const displayUrl = browserDisplayUrl(tab.url ?? null, devTunnelRecords);
  if (
    displayUrl &&
    isDevTunnelPreviewUrl(tab.url ?? "") &&
    (!tab.title || /(?:^|\b)(?:502|bad gateway|cmmd\.ai)(?:\b|$)/iu.test(tab.title))
  ) {
    return displayUrl;
  }
  return tab.title ?? displayUrl ?? `Tab ${index + 1}`;
}

export function browserTabDisplaySecondaryLabel(
  tab: BrowserTabSnapshot,
  index: number,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): string {
  const displayUrl = browserDisplayUrl(tab.url ?? null, devTunnelRecords) ?? tab.url;
  if (!displayUrl) {
    return `Tab ${index + 1}`;
  }
  try {
    const parsed = new URL(displayUrl);
    const path = `${parsed.pathname}${parsed.search}`;
    const normalizedPath = path && path !== "/" ? path : "";
    return `${parsed.hostname}${normalizedPath} · Tab ${index + 1}`;
  } catch {
    return `${displayUrl} · Tab ${index + 1}`;
  }
}

export function duplicateTabLabelSet(labels: readonly string[]): ReadonlySet<string> {
  const counts = new Map<string, number>();
  for (const label of labels) {
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return new Set([...counts].filter(([, count]) => count > 1).map(([label]) => label));
}

export function resolveBrowserConsoleSummary(input: {
  readonly logs:
    | ReadonlyArray<{ readonly level?: string | undefined; readonly message?: string | undefined }>
    | undefined;
  readonly meta: { readonly count?: number | undefined } | undefined;
}): BrowserConsoleSummary | null {
  if (!input.meta) {
    return null;
  }
  const logs = input.logs ?? [];
  const latest = logs.at(-1) ?? null;
  const highestLevel = logs.some((entry) => entry.level === "error")
    ? "error"
    : logs.some((entry) => entry.level === "warning")
      ? "warning"
      : logs.length > 0
        ? (latest?.level ?? null)
        : null;

  return {
    count: input.meta.count ?? logs.length,
    highestLevel,
    latestMessage: latest?.message ?? null,
  };
}

export function latestObservedDevServerPreviewUrl(
  observations: ReadonlyArray<BrowserPreviewObservedText>,
): string | null {
  let latestUrl: string | null = null;
  let latestObservedAtMs = -1;
  for (const observation of observations) {
    const url = extractLocalDevServerPreviewUrl(observation.text);
    if (!url) {
      continue;
    }
    const observedAtMs = observation.observedAt ? Date.parse(observation.observedAt) : Number.NaN;
    const safeObservedAtMs = Number.isFinite(observedAtMs) ? observedAtMs : 0;
    if (safeObservedAtMs >= latestObservedAtMs) {
      latestUrl = url;
      latestObservedAtMs = safeObservedAtMs;
    }
  }
  return latestUrl;
}

export function browserSessionDisplayName(session: BrowserSessionSnapshot | null): string {
  if (!session) {
    return "Browser session";
  }
  if (session.name) {
    return session.name;
  }
  switch (session.ownerRole) {
    case "worker":
      return "Worker browser";
    case "qa":
      return "QA browser";
    case "reviewer":
      return "Reviewer browser";
    case "user":
      return "User browser";
    case "lead":
    default:
      return "Lead browser";
  }
}

export function browserSessionSubtitle(
  session: BrowserSessionSnapshot | null,
  activeTab: BrowserTabSnapshot | null,
): string {
  if (!session) {
    return "No connected browser session";
  }
  const owner = session.agentId ? `${session.ownerRole} · ${session.agentId}` : session.ownerRole;
  const tabTitle = activeTab?.title ?? activeTab?.url ?? "No selected tab";
  return `${owner} · ${session.visibility} · ${tabTitle}`;
}

export function resolveBrowserPreviewStateChromeVm(input: {
  readonly status: BrowserPreviewStatus | string;
  readonly canRefresh: boolean;
  readonly canOpenExternal: boolean;
  readonly hasRefreshHandler: boolean;
  readonly hasOpenExternalHandler: boolean;
}): BrowserPreviewStateChromeVm {
  const isProblem =
    input.status === "failed" ||
    input.status === "runtime-unavailable" ||
    input.status === "navigation-failed";
  const isConnecting = input.status === "connecting" || input.status === "navigation-pending";
  const showRefresh = input.canRefresh && input.hasRefreshHandler;
  const showOpenExternal = input.canOpenExternal && input.hasOpenExternalHandler;

  return {
    isProblem,
    isConnecting,
    icon: isProblem
      ? "alert"
      : isConnecting
        ? "loader"
        : input.status === "capture-pending"
          ? "activity"
          : "globe",
    showActions: showRefresh || showOpenExternal,
    showRefresh,
    showOpenExternal,
  };
}

export function resolveBrowserActiveTabViewState(input: {
  readonly activeTab: BrowserTabSnapshot | null | undefined;
}): {
  readonly activeTabUrl: string | null;
  readonly activeRenderMode: BrowserRenderMode | null;
  readonly shouldPreferBrowserCapture: boolean;
} {
  const activeTabUrl = !isBlankBrowserUrl(input.activeTab?.url)
    ? (input.activeTab?.url ?? null)
    : null;
  const activeRenderMode =
    activeTabUrl !== null
      ? (input.activeTab?.renderMode ?? resolveBrowserRenderMode(activeTabUrl))
      : null;

  return {
    activeTabUrl,
    activeRenderMode,
    shouldPreferBrowserCapture: activeRenderMode === "capture",
  };
}

export function selectBrowserDisplayFrameSource(input: {
  readonly frameIsFresh: boolean;
  readonly frameDataUrl: string | undefined;
  readonly frameUrl: string | undefined;
  readonly frameCapturedAt: string | undefined;
  readonly frameViewport: BrowserViewport | undefined;
  readonly screenshotIsFresh: boolean;
  readonly screenshotDataUrl: string | undefined;
  readonly screenshotUrl: string | undefined;
  readonly screenshotCapturedAt: string | undefined;
  readonly screenshotViewport: BrowserViewport | undefined;
}): BrowserDisplayFrameSource {
  if (input.frameIsFresh) {
    return {
      dataUrl: input.frameDataUrl,
      url: input.frameUrl,
      capturedAt: input.frameCapturedAt,
      viewport: input.frameViewport,
    };
  }

  if (input.screenshotIsFresh) {
    return {
      dataUrl: input.screenshotDataUrl,
      url: input.screenshotUrl,
      capturedAt: input.screenshotCapturedAt,
      viewport: input.screenshotViewport,
    };
  }

  return {
    dataUrl: undefined,
    url: undefined,
    capturedAt: undefined,
    viewport: undefined,
  };
}

export function resolveBrowserLiveDisplayState(input: {
  readonly session: BrowserSessionSnapshot | null | undefined;
}): BrowserLiveDisplayState {
  const browserDisplay = input.session
    ? normalizeBrowserDisplayForPreview(input.session.display)
    : undefined;
  const isAttached =
    browserDisplay?.status === "connected" &&
    (browserDisplay.transport === "native-embedded" ||
      browserDisplay.transport === "remote-webrtc");

  // Which surface to mount is decided once, in
  // `@cmmd-forge/shared/browserPresentation`, from this display plus the render
  // mode, annotation state, paintability and client capability. Precomputing a
  // partial answer here is what let the panel and this module disagree.
  return { browserDisplay, isAttached };
}

export function resolveBrowserBusyState(input: {
  readonly connecting: boolean;
  readonly openingBrowserSession: boolean;
  readonly lifecycleAction: string | null;
  readonly pendingBrowserCommand: BrowserCommand["type"] | null;
  readonly hasSession: boolean;
}): BrowserBusyState {
  const commandBusy = input.pendingBrowserCommand !== null;
  const actionBusy = input.openingBrowserSession || input.lifecycleAction !== null || commandBusy;
  const busy = input.connecting || input.lifecycleAction !== null || commandBusy;

  return {
    commandBusy,
    actionBusy,
    busy,
    addressBusy: actionBusy || (input.connecting && input.hasSession),
  };
}

export function resolveBrowserInteractiveSurfaceViewport(input: {
  readonly browserDisplay: BrowserDisplaySession | undefined;
  readonly surfaceViewport: BrowserViewport | undefined;
  readonly sessionViewport: BrowserViewport | undefined;
  readonly displayFrameViewport: BrowserViewport | undefined;
}): BrowserViewport | undefined {
  if (input.browserDisplay?.transport === "remote-webrtc") {
    return input.surfaceViewport ?? input.browserDisplay.viewport ?? input.sessionViewport;
  }

  return input.displayFrameViewport;
}

export function buildBrowserCommentPrompt(input: {
  readonly note: string;
  readonly target: BrowserCommentTarget;
}): string {
  const lines = ["Browser comment", `URL: ${input.target.url}`, `Tab ID: ${input.target.tabId}`];
  if (input.target.captureId) {
    lines.push(`Capture ID: ${input.target.captureId}`);
  }
  if (input.target.capturedAt) {
    lines.push(`Captured At: ${input.target.capturedAt}`);
  }
  if (input.target.viewport) {
    lines.push(`Viewport: ${input.target.viewport.width}x${input.target.viewport.height}`);
  }
  if (input.target.point) {
    lines.push(`Point: ${input.target.point.x},${input.target.point.y}`);
  }
  if (input.target.rect) {
    lines.push(
      `Rect: ${input.target.rect.x},${input.target.rect.y},${input.target.rect.width},${input.target.rect.height}`,
    );
  }
  lines.push("", input.note.trim());
  return lines.join("\n");
}
